// ** React Imports
import React, { useContext } from 'react'
import {Link, Navigate, useNavigate} from 'react-router-dom'

// ** Custom Hooks
import { useSkin } from '@hooks/useSkin'

// ** Third Party Components
import toast from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { Coffee, X } from 'react-feather'
import jwt_decode from "jwt-decode";

// ** Actions
import { handleLogout } from '@store/authentication'

// ** Context
import { AbilityContext } from '@src/utility/context/Can'

// ** Custom Components
import Avatar from '@components/avatar'
import InputPasswordToggle from '@components/input-password-toggle'

// ** Utils
import { getHomeRouteForLoggedInUser } from '@utils'

// ** Reactstrap Imports
import {Row, Col, Form, Input, Label, Button, CardText, CardTitle, FormFeedback} from 'reactstrap'

// ** Styles
import '@styles/react/pages/page-authentication.scss'
import themeConfig from "../../../configs/themeConfig";
import axios from "axios";

const ToastContent = ({t, msg, color}) => {
    return (
        <div className='d-flex'>
            <div className='me-1'>
                <Avatar size='sm' color={color} icon={<Coffee size={12} />} />
            </div>
            <div className='d-flex flex-column'>
                <div className='d-flex justify-content-between'>
                    <X size={12} className='cursor-pointer' onClick={() => toast.dismiss(t.id)} />
                </div>
                <span>{msg}</span>
            </div>
        </div>
    )
}

const ChangePassword = () => {
    // ** Hooks
    const { skin } = useSkin()
    const passwordRules = (value) => {
        const regex = /^(?=.*[A-Z])(?=.*[\W_]).{8,}$/i;
        return (regex.test(value));
    };
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const ability = useContext(AbilityContext)
    const {
        control,
        setError,
        handleSubmit,
        formState: { errors }
    } = useForm({ mode: "onChange" } )
    const illustration = skin === 'dark' ? 'login-v2-dark.svg' : 'login-v2.svg',
        source = require(`@src/assets/images/pages/${illustration}`)

    const oldPasswordErrorMessages = ["La solicitud de actualización de contraseña no fue exitosa, la contraseña no es válida"];
    const newPasswordErrorMessages = ["La solicitud de actualización de contraseña no fue exitosa, la nueva contraseña no es válida"];

    const onLogout = () => {
        dispatch(handleLogout());
        navigate("/login")
    }
    const onSubmit = async dataForm => {
        if (Object.values(dataForm).every(field => field.length > 0)) {
            if (dataForm.oldPassword === dataForm.newPassword) {
                toast(t => (
                    <ToastContent t={t} msg="La contraseña anterior y la nueva no deben ser iguales" color={"danger"} />
                ))
                return;
            }

            if (dataForm.newPassword !== dataForm.confirmNewPassword) {
                toast(t => (
                    <ToastContent t={t} msg="la nueva contraseña no coincide" color={"danger"} />
                ))
                return;
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/users/password`, {
                'password': dataForm.oldPassword,
                'newPassword': dataForm.newPassword
            })

            if (response.data.header.code !== 200) {
                if (oldPasswordErrorMessages.includes(response.data.header.message)){
                    setError('oldPassword', { type: 'custom', message: `No coincide con la contraseña temporal asignada`})
                }
                if (newPasswordErrorMessages.includes(response.data.header.message)){
                    setError('newPassword', { type: 'custom', message: `La nueva contraseña no es válida`})
                    setError('confirmNewPassword', { type: 'custom', message: `La nueva contraseña no es válida`})
                }
                toast(t => (
                    <ToastContent t={t} msg={response.data.header.message} color={"danger"} />
                ))
            } else {
                toast(t => (
                    <ToastContent t={t} msg="Tu contraseña ha sido cambiada. ¡Por favor inicia sesión nuevamente! Gracias" color={"success"} />
                ))
                onLogout();
            }
        } else {
            for (const key in data) {
                if (data[key].length === 0) {
                    setError(key, {
                        type: 'manual'
                    })
                }
            }
        }
    }

    return (
        <div className='auth-wrapper auth-cover'>
            <Row className='auth-inner m-0'>
                <Link className='brand-logo' to='/' onClick={e => e.preventDefault()}>
                    <h1 className='brand-text text-primary ms-1'>{themeConfig.app.appName}</h1>
                </Link>
                <Col className='d-none d-lg-flex align-items-center p-5' lg='7' sm='12'>
                    <div className='w-100 d-lg-flex align-items-center justify-content-center px-5'>
                        <img className='img-fluid' src={source} alt='Login Cover' />
                    </div>
                </Col>
                <Col className='d-flex align-items-center auth-bg px-2 p-lg-5' lg='5' sm='12'>
                    <Col className='px-xl-2 mx-auto' sm='8' md='6' lg='12'>
                        <CardTitle tag='h2' className='fw-bold mb-1'>
                            Bienvenido! 👋
                        </CardTitle>
                        <CardText className='mb-2'>Inicio de sesión por primera vez: establezca su nueva contraseña e inicie sesión una y otra vez</CardText>
                        <Form className='auth-login-form mt-2' onSubmit={handleSubmit(onSubmit)}>
                            <div className='mb-1'>
                                <div className='d-flex justify-content-between'>
                                    <Label className='form-label' for='oldPassword'>
                                        Ingresar contraseña temporal otorgada
                                    </Label>
                                    {/*<Link to='/forgot-password'>
                      <small>Forgot Password?</small>
                    </Link>*/}
                                </div>
                                <Controller
                                    id='oldPassword'
                                    name='oldPassword'
                                    rules={{ validate: { passwordRules } }}
                                    control={control}
                                    render={({ field }) => (
                                        <InputPasswordToggle
                                            className='input-group-merge'
                                            placeholder=' '
                                            invalid={errors.oldPassword && true}
                                            {...field} />
                                    )}
                                />
                                {errors.oldPassword && <FormFeedback>No coincide con la contraseña temporal asignada</FormFeedback>}
                            </div>
                            <div className='mb-1'>
                                <div className='d-flex justify-content-between'>
                                    <Label className='form-label' for='newPassword'>
                                        Ingresar una nueva contraseña
                                    </Label>
                                    {/*<Link to='/forgot-password'>
                      <small>Forgot Password?</small>
                    </Link>*/}
                                </div>
                                <Controller
                                    id='newPassword'
                                    name='newPassword'
                                    rules={{ validate: { passwordRules } }}
                                    control={control}
                                    render={({ field }) => (
                                        <InputPasswordToggle
                                            className='input-group-merge'
                                            placeholder=' '
                                            invalid={errors.newPassword && true}
                                            {...field} />
                                    )}
                                />
                                {errors.newPassword && <FormFeedback>Se debe colocar una contraseña distinta a la temporal.
                                    Debe de tener 8 caracteres o más.
                                    Debe de utilizar por lo menos un caracter especial y mayúscula</FormFeedback>}
                            </div>

                            <div className='mb-1'>
                                <div className='d-flex justify-content-between'>
                                    <Label className='form-label' for='confirmNewPassword'>
                                        Ingresar nuevamente la nueva contraseña
                                    </Label>
                                    {/*<Link to='/forgot-password'>
                      <small>Forgot Password?</small>
                    </Link>*/}
                                </div>
                                <Controller
                                    id='confirmNewPassword'
                                    name='confirmNewPassword'
                                    rules={{ validate: { passwordRules } }}
                                    control={control}
                                    render={({ field }) => (
                                        <InputPasswordToggle
                                            className='input-group-merge'
                                            placeholder=' '
                                            invalid={errors.confirmNewPassword && true}
                                            {...field} />
                                    )}
                                />
                                {errors.confirmNewPassword && <FormFeedback>Se debe colocar una contraseña distinta a la temporal.
                                    Debe de tener 8 caracteres o más.
                                    Debe de utilizar por lo menos un carácter especial y mayúscula.</FormFeedback>}
                            </div>
                            <Button type='submit' color='primary' block>
                                Ingresar
                            </Button>
                        </Form>
                        {/*<p className='text-center mt-2'>
                <span className='me-25'>Nuevo en la plataforma?</span>
                <Link to='/register'>
                  <span>Crear una cuenta</span>
                </Link>
              </p>*/}
                        <Button color='primary' block style={{marginTop: "4%"}} onClick={() => onLogout()}>
                            Logout
                        </Button>
                    </Col>
                </Col>
            </Row>
        </div>
    )
}

export default ChangePassword
